import { apiRequest } from './http';

/** Any enabled tenant currency can be selected; IRR/IRT use the tenant denomination policy. */
export type SupportedCurrencyCode = string;

export interface SupportedCurrency {
  code: SupportedCurrencyCode;
  name: string;
  isEnabled: boolean;
}

export interface CurrencyPreference {
  defaultCurrencyCode: SupportedCurrencyCode;
  usdToIrrRate: number | null;
  rialsPerToman: number;
  currencies: readonly SupportedCurrency[];
  updatedAt: string;
  version: number;
}

export interface UpdateCurrencyPreference {
  defaultCurrencyCode: SupportedCurrencyCode;
  usdToIrrRate: number;
  rialsPerToman: number;
}

/**
 * Returns a non-authoritative form default from the server-owned tenant currency policy.
 * Accounting conversion remains a backend responsibility; UI components must not embed denomination factors.
 */
export function configuredExchangeRate(
  sourceCurrencyCode: SupportedCurrencyCode,
  targetCurrencyCode: SupportedCurrencyCode,
  preference: Pick<CurrencyPreference, 'usdToIrrRate' | 'rialsPerToman'> | null | undefined,
): number | null {
  const source = sourceCurrencyCode.trim().toUpperCase();
  const target = targetCurrencyCode.trim().toUpperCase();
  if (source === target) return 1;
  const rialsPerToman = preference?.rialsPerToman;
  if (!Number.isFinite(rialsPerToman) || !rialsPerToman || rialsPerToman <= 0) return null;
  if (source === 'IRT' && target === 'IRR') return rialsPerToman;
  if (source === 'IRR' && target === 'IRT') return 1 / rialsPerToman;
  const usdToIrrRate = preference?.usdToIrrRate;
  if (source === 'USD' && usdToIrrRate && usdToIrrRate > 0) {
    if (target === 'IRR') return usdToIrrRate;
    if (target === 'IRT') return usdToIrrRate / rialsPerToman;
  }
  return null;
}

/** Returns every enabled tenant currency once, preserving the server-defined display order. */
export function enabledCurrencyCodes(preference: CurrencyPreference | null | undefined): SupportedCurrencyCode[] {
  const seen = new Set<string>();
  const result: SupportedCurrencyCode[] = [];
  for (const currency of preference?.currencies ?? []) {
    const code = currency.code.trim().toUpperCase();
    if (!currency.isEnabled || !code || seen.has(code)) continue;
    seen.add(code);
    result.push(code);
  }
  return result;
}

/** Chooses an editable exchange-rate pair without assuming the tenant only uses IRR, IRT, and USD. */
export function preferredCurrencyPair(preference: CurrencyPreference | null | undefined): {
  fromCurrencyCode: SupportedCurrencyCode;
  toCurrencyCode: SupportedCurrencyCode;
} {
  const codes = enabledCurrencyCodes(preference);
  const toCurrencyCode = codes.includes(preference?.defaultCurrencyCode ?? '')
    ? preference!.defaultCurrencyCode
    : codes[0] ?? 'IRR';
  const fromCurrencyCode = codes.includes('USD') && toCurrencyCode !== 'USD'
    ? 'USD'
    : codes.find((code) => code !== toCurrencyCode) ?? toCurrencyCode;
  return { fromCurrencyCode, toCurrencyCode };
}

export const currencyPreferenceApi = {
  get(): Promise<CurrencyPreference> {
    return apiRequest<CurrencyPreference>('/currency-preferences');
  },

  update(input: UpdateCurrencyPreference): Promise<CurrencyPreference> {
    return apiRequest<CurrencyPreference>('/currency-preferences', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
};
